/**
 * Utility functions and types for service layer standardization
 * Provides consistent patterns for service function implementation
 */

import { logError, AppError, ErrorType } from '../error/errorHandler';
import { cacheManager } from '../cache/cacheManager';

/**
 * Standard operation result type for service functions
 * @template T - The type of data returned by the operation
 */
export interface ServiceResult<T> {
  data: T | null;
  error: AppError | null;
}

/**
 * Wraps a service operation with standard error handling and logging
 * 
 * @template T - Type of data returned by the operation
 * @param operation - Function that performs the service operation
 * @param operationName - Name of the operation for error logging
 * @returns The result of the operation, or null with appropriate error
 */
export async function executeServiceOperation<T>(
  operation: () => Promise<T>,
  operationName: string
): Promise<ServiceResult<T>> {
  try {
    const data = await operation();
    return { data, error: null };
  } catch (err) {
    const error = err instanceof AppError 
      ? err 
      : new AppError({
          message: `Error during ${operationName}: ${err instanceof Error ? err.message : 'Unknown error'}`,
          type: ErrorType.UNKNOWN,
          cause: err
        });
    
    // Log the error
    logError(error, operationName);
    
    return { data: null, error };
  }
}

/**
 * Helper function to transform database rows into a specific format
 * 
 * @template T - Type of input data
 * @template R - Type of output data
 * @param data - Data to transform
 * @param transformer - Function to transform the data
 * @returns Transformed data
 */
export function transformData<T, R>(data: T[], transformer: (item: T) => R): R[] {
  return data.map(transformer);
}

/**
 * Safely gets a cached value or executes the retrieval function
 * 
 * @template T - Type of data to retrieve
 * @param cacheKey - Cache key to check
 * @param retrievalFn - Function to call if cache miss
 * @param ttl - Time to live for cached value in seconds
 * @returns Retrieved data
 */
export async function getWithCache<T>(
  cacheKey: string,
  retrievalFn: () => Promise<T>,
  ttl: number
): Promise<T> {
  // Check cache first
  const cachedValue = await cacheManager.get<T>(cacheKey);
  if (cachedValue) {
    return cachedValue;
  }
  
  // Fetch data if not in cache
  const data = await retrievalFn();
  
    // Cache the result ONLY if it's not null/undefined
    if (data !== null && data !== undefined) {
      await cacheManager.set(cacheKey, data, ttl);
    }
  return data;
} 